import React, { useState, useEffect } from "react";
import axios from "axios";
import { Lock, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { useNavigate, useSearchParams } from "react-router-dom";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Login = () => {
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [autoLoginAttempted, setAutoLoginAttempted] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Auto-login with URL parameter
  useEffect(() => {
    const autoLogin = async () => {
      const pwParam = searchParams.get("pw");
      
      if (pwParam && !autoLoginAttempted) {
        setAutoLoginAttempted(true);
        setLoading(true);
        
        try {
          // Decode base64 password
          const decodedPassword = atob(pwParam);
          
          const response = await axios.post(`${API}/auth/verify-password`, {
            password: decodedPassword
          });

          if (response.data.success) {
            sessionStorage.setItem("hyk_auth", "authenticated");
            navigate("/");
          } else {
            toast.error("Otomatik giriş başarısız - yanlış şifre");
          }
        } catch (error) {
          console.error("Auto-login error:", error);
          toast.error("Otomatik giriş başarısız");
        } finally {
          setLoading(false);
        }
      }
    };

    autoLogin();
  }, [searchParams, navigate, autoLoginAttempted]);

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!password) {
      toast.error("Lütfen şifre girin");
      return;
    }

    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/verify-password`, {
        password: password
      });

      if (response.data.success) {
        // Store auth token in sessionStorage
        sessionStorage.setItem("hyk_auth", "authenticated");
        toast.success("Giriş başarılı!");
        navigate("/");
      } else {
        toast.error(response.data.message || "Yanlış şifre");
        setPassword("");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Giriş yapılırken bir hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <Card className="glass-card shadow-2xl">
          <CardHeader className="text-center space-y-2">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              HYK PDF Veri Çıkarma
            </CardTitle>
            <CardDescription className="text-base">
              Devam etmek için şifrenizi girin
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Şifre
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Şifrenizi girin"
                    className="pr-10 h-12 text-base"
                    autoFocus
                    disabled={loading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                className="w-full h-12 text-base bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white font-semibold shadow-lg"
                disabled={loading}
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Giriş yapılıyor...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Lock className="w-5 h-5" />
                    Giriş Yap
                  </span>
                )}
              </Button>
            </form>

            <div className="mt-6 pt-6 border-t border-gray-200">
              <p className="text-xs text-gray-500 text-center">
                Bu sistem şifre ile korumalıdır. Yetkisiz erişim yasaktır.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
